/**
 * Tipos do componente `flux.detail@1` — espelham o contrato de `@flux/iara-core`
 * (`components/manifests/flux-detail-v1.ts`), mantidos no client por desacoplamento
 * (mesmo critério de `flux-list.model.ts`).
 *
 * `data`/`presentation` chegam como `unknown` (JSON do backend); os guards fazem o
 * narrowing seguro antes de renderizar — payload inesperado vira `null` e o renderer
 * cai no `fallbackText`. Os valores já vêm formatados pelo modelo (sem UUID/SNAKE_CASE
 * crus), então o cliente apenas exibe.
 */

export type FluxDetailValueKind = 'text' | 'date' | 'datetime' | 'date_relative' | 'link' | 'badge';

export interface FluxDetailField {
  label: string;
  value: string;
  valueKind: FluxDetailValueKind;
}

export type FluxDetailStatusTheme = 'blue' | 'default' | 'green' | 'magenta' | 'orange' | 'red';

export interface FluxDetailData {
  entityType: string;
  entityId: string;
  title: string;
  status?: string;
  fields: FluxDetailField[];
  canonicalUrl?: string;
  narrative?: string;
}

export interface FluxDetailPresentation {
  statusTheme?: FluxDetailStatusTheme;
}

const VALUE_KINDS = new Set<FluxDetailValueKind>([
  'text',
  'date',
  'datetime',
  'date_relative',
  'link',
  'badge',
]);

const STATUS_THEMES = new Set<FluxDetailStatusTheme>([
  'blue',
  'default',
  'green',
  'magenta',
  'orange',
  'red',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Narrowing de `flux.detail@1` data. Valida o shape mínimo para renderizar
 * (entityType/entityId/title + fields[] com label/value). Retorna `null` em
 * qualquer divergência para o renderer cair no fallback.
 */
export function asFluxDetailData(data: unknown): FluxDetailData | null {
  if (!isRecord(data)) return null;
  if (!isString(data['entityType']) || data['entityType'].length === 0) return null;
  if (!isString(data['entityId']) || data['entityId'].length === 0) return null;
  if (!isString(data['title']) || data['title'].length === 0) return null;
  if (!Array.isArray(data['fields'])) return null;

  const fields: FluxDetailField[] = data['fields']
    .filter(
      (field): field is Record<string, unknown> =>
        isRecord(field) && isString(field['label']) && isString(field['value']),
    )
    .map((field) => ({
      label: field['label'] as string,
      value: field['value'] as string,
      valueKind: VALUE_KINDS.has(field['valueKind'] as FluxDetailValueKind)
        ? (field['valueKind'] as FluxDetailValueKind)
        : 'text',
    }));

  return {
    entityType: data['entityType'],
    entityId: data['entityId'],
    title: data['title'],
    fields,
    ...(isString(data['status']) ? { status: data['status'] } : {}),
    ...(isString(data['canonicalUrl']) ? { canonicalUrl: data['canonicalUrl'] } : {}),
    ...(isString(data['narrative']) ? { narrative: data['narrative'] } : {}),
  };
}

/**
 * Narrowing de `flux.detail@1` presentation. É opcional (o backend pode omitir),
 * então um payload inválido vira `null` e o renderer usa os defaults.
 */
export function asFluxDetailPresentation(presentation: unknown): FluxDetailPresentation | null {
  if (!isRecord(presentation)) return null;
  const theme = presentation['statusTheme'];
  if (!STATUS_THEMES.has(theme as FluxDetailStatusTheme)) return {};
  return { statusTheme: theme as FluxDetailStatusTheme };
}
